import { Box, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { Trans, useTranslation } from 'react-i18next';
import CaptionedLinearLoader from 'pages/calculator/MediaPlanning/components/Touchpoints/CaptionedLinearLoader/CaptionedLinearLoader';
import SlidersError from 'pages/calculator/MediaPlanning/components/Touchpoints/SlidersError/SlidersError';
import { DIGITAL_FAIR_VALUE_KEY } from 'api/services/digitalFairValue';
import TargetGroupSelectors from 'pages/calculator/MediaPlanning/components/Touchpoints/YouTubeTouchpoint/TargetGroupSelectors/TargetGroupSelectors';
import PartnersSelectors from 'pages/calculator/MediaPlanning/components/Touchpoints/YouTubeTouchpoint/PartnersSelectors/PartnersSelectors';
import { useEffect } from 'react';
import usePlanStore, { YouTubeConfig } from '../../../../../../store/planStore';
import useYoutubeConfig from '../hooks/useYoutubeConfig';
import useYoutubeSliders from '../hooks/useYoutubeSliders';
import { YTTargetGroup } from '../interfaces';
import { getPartners } from '../../../../../../api/services/mediaPlanning/getPartners';

function YouTubeTouchpoint() {
  const { t } = useTranslation();

  const {
    countryForms,
    selectedCountry,
    updateYouTubeConfig,
    selectedCountryISOCode,
    generalInfoState,
  } = usePlanStore();

  const youTubeConfig: YouTubeConfig | undefined =
    countryForms[selectedCountry!]?.youTubeConfig;

  const {
    fetchYoutubeConfig,
    data: fieldConfig,
    isFetching: isFieldConfigFetching,
  } = useYoutubeConfig();

  useEffect(() => {
    if (!countryForms[selectedCountry!].youTubeConfig?.targetGroup) {
      fetchYoutubeConfig(undefined);
    }
  }, [countryForms[selectedCountry!].youTubeConfig?.targetGroup]);

  const {
    handleTargetGroupChange,
    handleSaturationChange,
    handleUnSkipChange,
    handleBumperChange,
  } = useYoutubeSliders(youTubeConfig);

  const handleChangeStateValues = (key: string, value: any) => {
    switch (key) {
      case 'bumperValue':
        handleBumperChange(value as number);
        break;
      case 'unSkipValue':
        handleUnSkipChange(value as number);
        break;
      case 'targetGroup':
        handleTargetGroupChange(value as YTTargetGroup);
        fetchYoutubeConfig(value);
        break;
      case 'targetGroupSaturation':
        handleSaturationChange(value as number);
        break;
      default:
        updateYouTubeConfig(selectedCountry!, { [key]: value });
        break;
    }
  };

  const {
    data: { data: fetchedPartnersData } = {},
    isLoading: isPartnersDataLoading,
    isError: isPartnersDataFetchingError,
  } = useQuery(
    [
      DIGITAL_FAIR_VALUE_KEY,
      'partners-yt',
      selectedCountryISOCode,
      generalInfoState.division,
      generalInfoState.brand,
    ],
    () =>
      getPartners(
        selectedCountryISOCode,
        generalInfoState.division,
        generalInfoState.brand
      )
  );

  if (isPartnersDataFetchingError) {
    return <SlidersError error={t('mediaPlanning.loadingError')} />;
  }

  if (isPartnersDataLoading || isFieldConfigFetching) {
    return (
      <CaptionedLinearLoader
        caption={
          <Typography textAlign="center">
            <Trans i18nKey="loaders.holdOn" />
          </Typography>
        }
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pt: '13px',
        pb: '20px',
        rowGap: '38px',
      }}
    >
      <TargetGroupSelectors
        targetGroupSaturation={youTubeConfig?.saturation}
        skipCpvValue={youTubeConfig?.skipValue}
        unSkipValue={youTubeConfig?.unSkipValue}
        bumperValue={youTubeConfig?.bumperValue}
        handleChangeStateValues={handleChangeStateValues}
        minMaxSelectorsValues={countryForms[selectedCountry!].youTubeConfig}
      />

      <PartnersSelectors
        partners={youTubeConfig?.partners || []}
        partnerBudgetShare={youTubeConfig?.partnerBudgetShare}
        fetchedPartnersData={fetchedPartnersData}
        handleChangeStateValues={handleChangeStateValues}
      />
    </Box>
  );
}

export default YouTubeTouchpoint;
